import React, { useState, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { Player } from '@/lib/types';
import GraduationYearWarningModal from './GraduationYearWarningModal';
import DuplicateEmailWarningModal from './DuplicateEmailWarningModal';
import MissingVariableWarningModal from './MissingVariableWarningModal';

interface MissingVariableIssue {
  playerName: string;
  playerId: number;
  missingVariables: { variable: string; label: string; }[];
}

interface WarningManagerProps {
  // Check functions
  onCheckGraduationWarnings: (emailTags: string[]) => Promise<any>;
  onCheckDuplicateEmails: (emailTags: string[]) => Promise<any>;
  onValidateMissingVariables: (content: string, subject: string, players: Player[]) => MissingVariableIssue[];
  
  // Final action
  onSendEmail: (emailData: any) => void;
  
  // Player data
  players: Player[];
  
  // Note: emailData is passed via processWarnings method, not as a prop
}

export interface WarningManagerRef {
  processWarnings: (emailData: any) => Promise<void>;
}

const WarningManager = React.forwardRef<WarningManagerRef, WarningManagerProps>(({
  onCheckGraduationWarnings,
  onCheckDuplicateEmails,
  onValidateMissingVariables,
  onSendEmail,
  players
}, ref) => {
  const [currentStep, setCurrentStep] = useState<'none' | 'missing-variables' | 'graduation' | 'duplicates'>('none');
  const [emailData, setEmailData] = useState<any>(null);
  
  // Warning states
  const [showGraduationWarning, setShowGraduationWarning] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showMissingVariableWarning, setShowMissingVariableWarning] = useState(false);
  
  // Warning data
  const [flaggedPlayers, setFlaggedPlayers] = useState<any[]>([]);
  const [duplicateRecipients, setDuplicateRecipients] = useState<any[]>([]);
  const [missingVariableIssues, setMissingVariableIssues] = useState<MissingVariableIssue[]>([]);

  const processWarnings = useCallback(async (data: any) => {
    setEmailData(data);
    
    try {
      // Step 1: Check for missing variables first
      if (data.emailTags && data.emailTags.length > 0) {
        const missingVarIssues = onValidateMissingVariables(
          data.content,
          data.subject,
          players
        );
        
        if (missingVarIssues.length > 0) {
          setMissingVariableIssues(missingVarIssues);
          setCurrentStep('missing-variables');
          setShowMissingVariableWarning(true);
          return;
        }
      }
      
      // Step 2: Check graduation warnings
      await checkGraduationWarnings(data);
      
    } catch (error) {
      console.error('Error processing warnings:', error);
      toast({
        title: "Warning check failed",
        description: "Proceeding with email send despite warning check failure.",
        variant: "destructive",
      });
      onSendEmail(data);
    }
  }, [onValidateMissingVariables, onCheckGraduationWarnings, players]);

  const checkGraduationWarnings = async (data: any) => {
    try {
      if (data.emailTags && data.emailTags.length > 0) {
        const result = await onCheckGraduationWarnings(data.emailTags);
        
        if (result.requires_warning && result.flagged_players.length > 0) {
          setFlaggedPlayers(result.flagged_players);
          setCurrentStep('graduation');
          setShowGraduationWarning(true);
          return;
        }
      }
      
      // Step 3: Check for duplicates
      await checkDuplicateEmails(data);
      
    } catch (error) {
      console.error('Graduation warning check failed:', error);
      // Continue to duplicate check even if graduation check fails
      await checkDuplicateEmails(data);
    }
  };

  const checkDuplicateEmails = async (data: any) => {
    try {
      if (data.emailTags && data.emailTags.length > 0) {
        const result = await onCheckDuplicateEmails(data.emailTags);
        
        if (result.requires_warning && result.duplicate_recipients.length > 0) {
          setDuplicateRecipients(result.duplicate_recipients);
          setCurrentStep('duplicates');
          setShowDuplicateWarning(true);
          return;
        }
      }
      
      // No warnings, proceed with send
      onSendEmail(data);
      resetState();
      
    } catch (error) {
      console.error('Duplicate email check failed:', error);
      // Proceed with send even if duplicate check fails
      onSendEmail(data);
      resetState();
    }
  };

  const resetState = () => {
    setCurrentStep('none');
    setEmailData(null);
    setShowGraduationWarning(false);
    setShowDuplicateWarning(false);
    setShowMissingVariableWarning(false);
    setFlaggedPlayers([]);
    setDuplicateRecipients([]);
    setMissingVariableIssues([]);
  };

  // Missing variable warning handlers
  const handleMissingVariableWarningClose = () => {
    resetState();
  };

  const handleMissingVariableWarningContinue = async (selectedPlayerIds?: number[]) => {
    setShowMissingVariableWarning(false);
    
    // If specific players were selected, filter the email data
    let updatedEmailData = emailData;
    if (selectedPlayerIds && selectedPlayerIds.length > 0) {
      const selectedEmails = players
        .filter(p => selectedPlayerIds.includes(p.id) && p.email)
        .map(p => p.email!)
        .filter(Boolean);
      
      updatedEmailData = {
        ...emailData,
        emailTags: selectedEmails,
        to: selectedEmails,
      };
    }
    
    // Continue to graduation warnings
    await checkGraduationWarnings(updatedEmailData);
  };

  // Graduation warning handlers
  const handleGraduationWarningProceed = async (selectedEmails: string[]) => {
    setShowGraduationWarning(false);
    
    const updatedEmailData = {
      ...emailData,
      emailTags: selectedEmails,
      to: selectedEmails,
    };
    
    // Continue to duplicate warnings
    await checkDuplicateEmails(updatedEmailData);
  };

  const handleGraduationWarningCancel = () => {
    resetState();
  };

  // Duplicate warning handlers
  const handleDuplicateWarningProceed = (selectedEmails: string[]) => {
    setShowDuplicateWarning(false);
    
    const updatedEmailData = {
      ...emailData,
      emailTags: selectedEmails,
      to: selectedEmails,
    };
    
    onSendEmail(updatedEmailData);
    resetState();
  };

  const handleDuplicateWarningCancel = () => {
    resetState();
  };

  const handlePlayerUpdate = (updatedPlayerData: any) => {
    // Update the flagged players list with the new data
    setFlaggedPlayers(prev => prev.map((player: any) => 
      player.id === updatedPlayerData.id 
        ? { ...player, ...updatedPlayerData }
        : player
    ));
    
    toast({
      title: "Player updated",
      description: `${updatedPlayerData.name || 'Player'}'s graduation year has been corrected.`,
    });
  };

  // Expose processWarnings method through ref
  React.useImperativeHandle(ref, () => ({
    processWarnings
  }));

  return (
    <>
      {/* Missing Variable Warning Modal - Highest priority */}
      <MissingVariableWarningModal
        isOpen={showMissingVariableWarning}
        onClose={handleMissingVariableWarningClose}
        onContinue={handleMissingVariableWarningContinue}
        issues={missingVariableIssues}
        players={players}
      />

      {/* Graduation Year Warning Modal */}
      <GraduationYearWarningModal
        isOpen={showGraduationWarning}
        onClose={handleGraduationWarningCancel}
        flaggedPlayers={flaggedPlayers}
        onProceedWithSelected={handleGraduationWarningProceed}
        onCancel={handleGraduationWarningCancel}
        onPlayerUpdate={handlePlayerUpdate}
      />

      {/* Duplicate Email Warning Modal */}
      <DuplicateEmailWarningModal
        isOpen={showDuplicateWarning}
        onClose={handleDuplicateWarningCancel}
        duplicateRecipients={duplicateRecipients}
        onProceedWithSelected={handleDuplicateWarningProceed}
        onCancel={handleDuplicateWarningCancel}
      />
    </>
  );
});

WarningManager.displayName = 'WarningManager';

export default WarningManager; 